import React, { useState, useEffect } from 'react';
import { useTranslations } from '@unisporkal/localization';
import { RawHtml } from '@unisporkal/utilities';
import { modalInteraction, ga4Track } from '@unisporkal/ga4';
import downloadFileFromUrl from '../../../../utils/downloadFileFromUrl';
import CheckMarkIcon from '../../../../icons/SmallCheckMark.svg?icon';
import styles from './BatchDownloadForm.module.scss';
import useBatchDownload from '../../../../hooks/queries/useBatchDownload';
import { MultiDownload } from '../../../../types/batchDownload.types';

interface MultiDownloadedState {
  [url: string]: { isClicked: boolean };
}

const MultiDownloads = () => {
  const t = useTranslations();
  const { response } = useBatchDownload();
  const { multiDownloads = [] } = response || {};
  const [multiDownloaded, setMultiDownloaded] = useState<MultiDownloadedState>(
    {}
  );

  const prepareMultiDownloads = () => {
    if (multiDownloads.length > 0) {
      const newMultiDownloaded: MultiDownloadedState = {};
      multiDownloads.forEach((item: MultiDownload) => {
        if (item.url) {
          newMultiDownloaded[item.url] = { isClicked: false };
        }
      });
      setMultiDownloaded(newMultiDownloaded);
    }
  };

  const triggerSingleDownload = () => {
    downloadFileFromUrl(document, multiDownloads[0].url);

    const modalEvent = modalInteraction({
      event: 'modal_cta_click',
      modal_content: 'download_options',
      modal_type: 'detailed_modal_with_cta',
      cta_text: 'Batch download',
      ui_element_location: 'modal_cta',
      modal_selection: 'batch_zip_link',
    });

    ga4Track(modalEvent);
  };

  const performAutoDownloads = () => {
    if (multiDownloads.length === 1) {
      triggerSingleDownload();
    }
  };

  useEffect(() => {
    prepareMultiDownloads();
    performAutoDownloads();
  }, []);

  const handleSingleClick = () => {
    triggerSingleDownload();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleSingleClick();
    }
  };

  const handleMultiClick = (url: string) => {
    setMultiDownloaded((prevState) => ({
      ...prevState,
      [url]: { isClicked: true },
    }));
  };

  if (multiDownloads.length === 1) {
    return (
      <section data-testid="multi-downloads">
        <div className={styles.linksContainer}>
          <div
            role="button"
            tabIndex={0}
            className={styles.singleLink}
            onClick={handleSingleClick}
            onKeyDown={handleKeyPress}
          >
            <RawHtml>
              {t('download_start_shortly_zip', {
                links: [{ href: '#', target: '_self' }],
              })}
            </RawHtml>
          </div>
          <div className={styles.singleLinkText}>
            {t('larger_files_take_time_to_download')}
          </div>
        </div>
      </section>
    );
  }

  if (multiDownloads.length > 1) {
    return (
      <section data-testid="multi-downloads">
        {multiDownloads.map((download, index) => (
          <div
            className={styles.linksContainer}
            key={download.url}
          >
            {index === 0 && (
              <span className={styles.linkLabel}>
                {t('start_downloading_zip_below')}
              </span>
            )}
            <div className={styles.linkContainer}>
              <a
                className={styles.link}
                href={download.url}
                onClick={() => handleMultiClick(download.url)}
              >
                {t('download_batch_count_assets', {
                  iterator: index + 1,
                  count: download.numberOfAssets,
                })}
              </a>
              {multiDownloaded[download.url]?.isClicked && (
                <div className={styles.checkMark}>
                  <CheckMarkIcon />
                </div>
              )}
            </div>
          </div>
        ))}
      </section>
    );
  }

  return null;
};

export default MultiDownloads;
